/**
 * @file date-time-helper.ts
 * @description Утилиты для работы с датами и временем в Task Manager приложении. Содержит функции для преобразования дат в миллисекунды UTC, обработки диапазонов дат, форматирования временных меток и конвертации между различными временными представлениями с использованием moment.js и поддержкой временных зон.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-17
 * @dependencies ['moment-timezone']
 * @related-files ['src/components/ui/date-picker', 'src/forms', 'src/api/actions', 'src/components/ui/tables']
 */

import moment from 'moment-timezone';

/**
 * Преобразует дату в формате строки в миллисекунды UTC
 * @param date - дата в формате строки
 * @returns количество миллисекунд с начала эпохи Unix
 */
export const getMilliseconds = (date: string) => {
    return moment.utc(date).valueOf();
};

/**
 * Преобразует диапазон дат в массив миллисекунд в формате строк
 * @param dateRangeValue - массив из двух дат в формате строк
 * @returns массив из двух значений: миллисекунды начальной и конечной даты или null
 */
export const getDateRangeMS = (dateRangeValue: string[]): (string | null)[] => {
    return [
        dateRangeValue[0] ? getMilliseconds(dateRangeValue[0]).toString() : null,
        dateRangeValue[1] ? getMilliseconds(dateRangeValue[1]).toString() : null
    ];
};

/**
 * Форматирует временную метку в читаемую строку
 * @param timestamp - временная метка в миллисекундах
 * @param format - формат вывода даты (по умолчанию 'DD.MM.YYYY HH:mm:ss')
 * @returns отформатированная строка даты или пустая строка для невалидных значений
 */
export const formatTimestamp = (timestamp: number | string, format: string = 'DD.MM.YYYY HH:mm:ss'): string => {
    if (Number(timestamp) <= 0) {
        return '';
    }

    return moment(timestamp).format(format);
};

/**
 * Преобразует временную метку в объект Date с учетом местной временной зоны
 * @param timestamp - временная метка в миллисекундах
 * @returns объект Date в местной временной зоне
 */
export const timestampToDate = (timestamp: number): Date => {
    const timezone = moment.tz.guess();

    return moment.utc(timestamp).tz(timezone, true).toDate();
};

/**
 * Преобразует объект Date в временную метку UTC
 * @param date - объект Date для преобразования
 * @returns временная метка в миллисекундах UTC
 */
export const dateToTimestamp = (date: Date): number => {
    return moment(date).utcOffset(0, true).valueOf();
};

/**
 * Преобразует строку даты в формате DD.MM.YYYY в timestamp в миллисекундах
 * @param dateString - строка даты в формате DD.MM.YYYY или null
 * @param format - формат входной строки (по умолчанию 'DD.MM.YYYY')
 * @returns timestamp в миллисекундах или null если входная строка null/невалидна
 */
export const stringToTimestamp = (dateString: string | null, format: string = 'DD.MM.YYYY'): number | null => {
    if (!dateString) {
        return null;
    }

    const parsedDate = moment.utc(dateString, format, true);

    if (!parsedDate.isValid()) {
        return null;
    }

    return parsedDate.valueOf();
};
